import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { MailFolderList } from 'owa-mail-folder-view';
import type { FolderForestTreeType, MailboxInfoInput } from 'owa-graph-schema';
import { generateDomPropertiesForAria, AriaProperties, AriaRoles } from 'owa-accessibility';
import { useLazyKeydownHandler } from 'owa-hotkeys';
import { LazyAction, LazyModule } from 'owa-bundling';
import MailFolderNodeTree from './MailFolderNodeTree';
import type { MailboxInfo } from 'owa-client-ids';
import type { MailFolderListWrapperFragment } from './__generated__/MailFolderListWrapper.interface';
import LoadMore from './LoadMore';
import { FetchPolicy } from '@apollo/client';

const lazyModule = new LazyModule(() => import('./lazyFolderListHotKeys'));
const lazySetupMailFolderListKeys = new LazyAction(lazyModule, m => m.setupMailFolderListKeys);

export interface MailFolderListWrapperProps extends React.HTMLProps<HTMLDivElement> {
    isFavoritesSupported: boolean; // this tree's folders can or can't support favorites for folder
    rootFolder: MailFolderListWrapperFragment;
    shouldShowLoadingSpinner: boolean;
    hasMoreData: boolean;
    treeType: FolderForestTreeType; // Type of folder tree
    rootNodeId: string;
    mailboxInfo: MailboxInfoInput;
    moveFolder: (
        destinationFolderId: string,
        destinationFolderMailboxInfo: MailboxInfo,
        sourceFolderId: string,
        sourceFolderMailboxInfo: MailboxInfo,
        sourceFolderParentFolderId: string,
        sourceFolderDisplayName: string
    ) => void;
    offset: number;
    fetchMore: (offset: number, fetchPolicy: FetchPolicy) => Promise<void>;
}

/**
 * Wrapper component that reads mobx store specific values for folder hierarchy
 */
export const MailFolderListWrapper = observer(function MailFolderListWrapper(
    props: MailFolderListWrapperProps
) {
    const containerRef = React.useRef<HTMLDivElement>();
    const { className, rootFolder, style, treeType, mailboxInfo, moveFolder } = props;

    // This is a hook
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2345 (50,27): Argument of type 'MutableRefObject<HTMLDivElement | undefined>' is not assignable to parameter of type 'RefObject<HTMLElement | null>'.
    // @ts-expect-error
    useLazyKeydownHandler(containerRef, lazySetupMailFolderListKeys.importAndExecute, {
        rootFolder: rootFolder,
        treeType: treeType,
    });

    const renderFolderNode = (
        folderId: string,
        nestDepth: number,
        treeType: FolderForestTreeType,
        isFavoritesSupported: boolean,
        onContextMenu: (
            evt: React.MouseEvent<unknown>,
            folderId: string,
            distinguishedFolderParentIds: string[]
        ) => void,
        distinguishedFolderParentIds: string[]
    ) => {
        return (
            <MailFolderNodeTree
                key={folderId}
                folderId={folderId}
                nestDepth={nestDepth}
                treeType={treeType}
                isFavoritesSupported={isFavoritesSupported}
                onContextMenu={onContextMenu}
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2322 (78,17): Type '(folderId: string, nestDepth: number, treeType: FolderForestTreeType, isFavoritesSupported: boolean, onContextMenu: (evt: React.MouseEvent<unknown>, folderId: string, distinguishedFolderParentIds: string[]) => void, distinguishedFolderParentIds: string[]) => JSX.Element' is not assignable to type 'RenderFolderNodeFunc'.
                // @ts-expect-error
                renderFolderNode={renderFolderNode}
                mailboxInfo={mailboxInfo}
                distinguishedFolderParentIds={distinguishedFolderParentIds}
                moveFolder={moveFolder}
            />
        );
    };

    const renderLoadNode = () => {
        return (
            <LoadMore
                fetchMore={props.fetchMore}
                shouldShowLoadingSpinner={props.shouldShowLoadingSpinner}
                hasMoreData={props.hasMoreData}
                offset={props.offset}
            />
        );
    };

    const mailFolderListProps = {
        ...props,
        rootFolderId: props.rootFolder?.id,
        renderFolderNode: renderFolderNode,
        childFolderIds: props.rootFolder?.childFolderIds,
        renderLoadNode: renderLoadNode,
        onLoadMoreClickedCallback: null,
    };

    const ariaProps: AriaProperties = {
        role: AriaRoles.tree,
    };

    return (
        <div
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2322 (115,13): Type 'MutableRefObject<HTMLDivElement | undefined>' is not assignable to type 'LegacyRef<HTMLDivElement> | undefined'.
            // @ts-expect-error
            ref={containerRef}
            style={style}
            className={className}
            {...generateDomPropertiesForAria(ariaProps)}>
            {
                /* Render tree */
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2322 (124,18): Type '{ rootFolderId: string; renderFolderNode: (folderId: string, nestDepth: number, treeType: FolderForestTreeType, isFavoritesSupported: boolean, onContextMenu: (evt: MouseEvent<unknown, MouseEvent>, folderId: string, distinguishedFolderParentIds: string[]) => void, distinguishedFolderParentIds: string[]) => Element; ....' is not assignable to type 'MailFolderListProps'.
                // @ts-expect-error
                <MailFolderList {...mailFolderListProps} />
            }
        </div>
    );
});
